import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useReveal } from '../hooks/useReveal';
import ParallaxHero from '../components/ParallaxHero';

export default function About() {
  useReveal();
  return (
    <>
      <Helmet>
        <title>About ITClave | India's Trusted Software Development Company</title>
        <meta name="description" content="Learn about ITClave — India's trusted software development and digital marketing company. 200+ projects, 150+ clients across India. Our mission, vision and values."/>
        <meta name="keywords" content="about ITClave, software development company India, IT company India, web development agency India"/>
      </Helmet>

      <ParallaxHero
        page="about"
        eyebrow="Our Story"
        title="About ITClave"
        subtitle="India's trusted IT company — delivering world-class digital solutions since 2026."
        cta={false}
      />

      <section style={{background:'#fff',padding:'80px 0'}}>
        <div className="container">
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:64,alignItems:'center'}} className="ab-2col">
            <div className="rl">
              <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=700&q=80" alt="ITClave team" loading="lazy" style={{width:'100%',borderRadius:24,boxShadow:'0 24px 80px rgba(123,82,171,.18)'}}/>
            </div>
            <div className="rr">
              <div className="eyebrow">Our Story</div>
              <h2 className="sec-title">Building Digital India, <span className="gt">One Business at a Time</span></h2>
              <p style={{color:'var(--text2)',fontSize:'.95rem',lineHeight:1.82,marginBottom:16}}>Founded in 2026, ITClave started with a simple mission: to bring world-class software development and digital marketing to Indian businesses at prices that make sense. What began as a small passionate team has grown into a 50+ strong company serving 150+ clients across India.</p>
              <p style={{color:'var(--text2)',fontSize:'.95rem',lineHeight:1.82,marginBottom:28}}>We've delivered 200+ projects — from startup MVPs and e-commerce stores to large enterprise ERP systems — for clients in Delhi, Mumbai, Bangalore, Hyderabad, Pune, Ahmedabad, Kolkata, and across the country. Always on time, always transparent, always results-driven.</p>
              <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
                <Link to="/contact" className="btn-main">🚀 Work With Us</Link>
                <Link to="/portfolio" className="btn-out">📂 See Our Work</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section style={{background:'var(--bg3)',padding:'80px 0'}}>
        <div className="container">
          <div className="sec-hdr-center"><div className="eyebrow" style={{justifyContent:'center'}}>Our Purpose</div><h2 className="sec-title">Mission, Vision <span className="gt">&amp; Values</span></h2></div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:24,marginBottom:48}} className="mv-2col">
            {[{icon:'🎯',title:'Our Mission',sub:'What We Do Every Day',color:'linear-gradient(135deg,#E8315B,#7B52AB,#4BB8E8)',text:'To empower businesses across India with cutting-edge digital technology — making world-class software development and digital marketing accessible and affordable for every entrepreneur, startup, and enterprise, regardless of their size or location.'},
              {icon:'🔭',title:'Our Vision', sub:'Where We\'re Going',   color:'linear-gradient(135deg,#4BB8E8,#7B52AB)',text:'To become India\'s most trusted IT partner — known for innovation, transparency, and measurable impact. We envision a digitally empowered India where every business, from a tier-3 city startup to a pan-India enterprise, competes and wins online.'}
            ].map(c=>(
              <div key={c.title} className="reveal" style={{borderRadius:28,padding:'44px 40px',background:c.color,position:'relative',overflow:'hidden',transition:'all .3s'}} onMouseOver={e=>{e.currentTarget.style.transform='translateY(-4px)';e.currentTarget.style.boxShadow='var(--sh-xl)';}} onMouseOut={e=>{e.currentTarget.style.transform='';e.currentTarget.style.boxShadow='none';}}>
                <div style={{position:'absolute',top:-50,right:-50,width:180,height:180,borderRadius:'50%',background:'rgba(255,255,255,.07)'}}/>
                <div style={{fontSize:'2.8rem',marginBottom:14}}>{c.icon}</div>
                <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:'1.5rem',fontWeight:900,color:'#fff',marginBottom:5}}>{c.title}</h3>
                <div style={{color:'rgba(255,255,255,.6)',fontSize:'.74rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',marginBottom:16}}>{c.sub}</div>
                <p style={{color:'rgba(255,255,255,.88)',fontSize:'.95rem',lineHeight:1.82}}>{c.text}</p>
              </div>
            ))}
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:18}} className="vals-grid">
            {[{icon:'💡',t:'Innovation',d:'Constantly exploring new technologies to solve real business challenges creatively.'},
              {icon:'🔍',t:'Transparency',d:'Open communication, honest timelines, clear pricing — no surprises, ever.'},
              {icon:'🎖️',t:'Excellence',d:'Every line of code, every campaign, every pixel meets the highest standard.'},
              {icon:'🌱',t:'Growth',d:'Our clients\' growth is our growth. We build long-term partnerships, not transactions.'}
            ].map((v,i)=>(
              <div key={i} className="reveal" style={{background:'#fff',border:'1.5px solid var(--border2)',borderRadius:20,padding:24,textAlign:'center',transition:'all .3s',transitionDelay:`${i*.1}s`}} onMouseOver={e=>{e.currentTarget.style.borderColor='var(--purple)';e.currentTarget.style.background='var(--purple-lt)';e.currentTarget.style.transform='translateY(-4px)';}} onMouseOut={e=>{e.currentTarget.style.borderColor='var(--border2)';e.currentTarget.style.background='#fff';e.currentTarget.style.transform='';}}>
                <div style={{fontSize:'1.7rem',marginBottom:9}}>{v.icon}</div>
                <div style={{fontFamily:"'Outfit',sans-serif",fontWeight:800,fontSize:'.92rem',color:'var(--text)',marginBottom:5}}>{v.t}</div>
                <div style={{fontSize:'.8rem',color:'var(--text2)',lineHeight:1.65}}>{v.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{background:'var(--grad)',padding:'64px 0'}}>
        <div className="container">
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:20,textAlign:'center'}} className="stats-grid">
            {[['200+','Projects Delivered'],['150+','Clients Across India'],['8+','Years Experience'],['4.9★','Google Rating']].map(([n,l],i)=>(
              <div key={i} style={{padding:20}}>
                <div style={{fontFamily:"'Outfit',sans-serif",fontSize:'2.5rem',fontWeight:900,color:'#fff',letterSpacing:'-.04em',lineHeight:1}}>{n}</div>
                <div style={{fontSize:'.78rem',color:'rgba(255,255,255,.75)',fontWeight:600,textTransform:'uppercase',letterSpacing:'.07em',marginTop:6}}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <style>{`
        @media(max-width:1024px){.ab-2col,.mv-2col{grid-template-columns:1fr!important}.vals-grid,.stats-grid{grid-template-columns:repeat(2,1fr)!important}}
        @media(max-width:480px){.vals-grid,.stats-grid{grid-template-columns:1fr 1fr!important}}
      `}</style>
    </>
  );
}
